import { useQuery } from "@tanstack/react-query";
import { useApiAuth } from "./useApiAuth";
import type { PoolCreationOptions } from "../types/pools";
import { apiUrl } from "../lib/api";
import {
  canonicalCompetitionCode,
  canonicalCompetitionDisplayName,
} from "@sportex100/shared/utils/competitionIdentity.ts";

function sanitizePoolOptions(payload: PoolCreationOptions): PoolCreationOptions {
  return {
    ...payload,
    sports: payload.sports.map((sport) => ({
      ...sport,
      competitions: sport.competitions.map((competition) => {
        const competitionCode = canonicalCompetitionCode(competition.competition_code);
        return {
          ...competition,
          competition_code: competitionCode,
          competition_name:
            canonicalCompetitionDisplayName(competitionCode, competition.competition_name) ??
            competition.competition_name,
        };
      }),
    })),
  };
}

export function usePoolOptions() {
  const { acquireToken } = useApiAuth();

  const query = useQuery({
    queryKey: ["pool-options"],
    queryFn: async () => {
      const accessToken = await acquireToken();
      if (!accessToken) {
        throw new Error("Authentication failed.");
      }

      const response = await fetch(apiUrl("/api/pools/options"), {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || "Failed to load pool options.");
      }
      const payload: PoolCreationOptions = await response.json();
      return sanitizePoolOptions(payload);
    },
  });

  return {
    poolOptions: query.data ?? null,
    isLoading: query.isLoading,
    error: query.error instanceof Error ? query.error.message : null,
    reload: async () => {
      await query.refetch();
    },
  };
}
